import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Clock, Calendar, MapPin, Users, LogOut } from 'lucide-react';

const API = '/api/volunteer-portal';

interface VolunteerInfo {
  _id: string; name: string; phone?: string; email?: string;
  skills?: string[]; area?: string; availability?: string;
  totalHours: number; hoursLog?: any[];
}

interface Shift {
  _id: string; title: string; date: string; startTime: string; endTime: string;
  location?: string; projectName?: string; maxVolunteers?: number;
  assignments: any[]; status: string;
}

// ─── Login Screen ─────────────────────────────────────────────────────────────
const LoginScreen: React.FC<{ orgId: string; onLogin?: (token: string, vol: any) => void }> = ({ orgId }) => {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await axios.post(`${API}/send-magic-link`, { email, orgId });
      setSent(true);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'שגיאה בשליחת הקישור');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0D47A1] via-[#1565C0] to-[#1976D2] flex items-center justify-center p-4" dir="rtl">
      <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-sm">
        <div className="text-center mb-6">
          <div className="text-4xl mb-2">🤝</div>
          <h1 className="text-xl font-bold text-gray-800">פורטל מתנדבים</h1>
          <p className="text-gray-500 text-sm mt-1">הזן את המייל שלך לקבלת קישור כניסה</p>
        </div>
        {sent ? (
          <div className="text-center py-4">
            <div className="text-4xl mb-3">📬</div>
            <p className="font-semibold text-gray-800">הקישור נשלח!</p>
            <p className="text-gray-500 text-sm mt-1">בדוק את תיבת הדואר שלך ולחץ על הקישור לכניסה.</p>
            <p className="text-gray-400 text-xs mt-2">תקף ל-2 שעות</p>
          </div>
        ) : (
          <form onSubmit={handleSend} className="space-y-4">
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus
              placeholder="your@email.com" dir="ltr"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-[#1565C0] outline-none" />
            {error && <p className="text-red-500 text-sm text-center">⚠️ {error}</p>}
            <button type="submit" disabled={loading}
              className="w-full bg-[#1565C0] hover:bg-[#0D47A1] text-white font-bold py-3 rounded-xl disabled:opacity-50">
              {loading ? 'שולח...' : 'שלח קישור כניסה →'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

// ─── Portal App ───────────────────────────────────────────────────────────────
const PortalApp: React.FC<{ token: string; vol: VolunteerInfo; onLogout: () => void }> = ({ token, vol: initialVol, onLogout }) => {
  const [tab, setTab] = useState<'shifts' | 'my-shifts' | 'tasks' | 'group' | 'profile'>('shifts');
  const [tasks, setTasks] = useState<any[]>([]);
  const [taskNote, setTaskNote] = useState<Record<string, string>>({});
  const [taskBusy, setTaskBusy] = useState<string | null>(null);

  // Groups (madrich attendance)
  const [groups, setGroups] = useState<any[]>([]);
  const [groupId, setGroupId] = useState<string>('');
  const [attDate, setAttDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [attEntries, setAttEntries] = useState<Record<string, { status: string; note: string }>>({});
  const [attMsg, setAttMsg] = useState<{ msg: string; ok: boolean } | null>(null);
  const [attSaving, setAttSaving] = useState(false);
  const [vol, setVol] = useState(initialVol);
  const [openShifts, setOpenShifts] = useState<Shift[]>([]);
  const [myShifts, setMyShifts] = useState<Shift[]>([]);
  const [totalHours, setTotalHours] = useState(vol.totalHours || 0);
  const [hoursLog, setHoursLog] = useState<any[]>(vol.hoursLog || []);
  const [loading, setLoading] = useState(false);
  const [signupMsg, setSignupMsg] = useState<{ id: string; msg: string; ok: boolean } | null>(null);

  // Self-report hours form
  const [reportHours, setReportHours] = useState('');
  const [reportDate, setReportDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [reportDesc, setReportDesc] = useState('');
  const [reportingMsg, setReportingMsg] = useState<{ msg: string; ok: boolean } | null>(null);
  const [submittingHours, setSubmittingHours] = useState(false);

  // Profile editing
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileDraft, setProfileDraft] = useState({
    phone: vol.phone || '', area: vol.area || '', availability: vol.availability || '',
    skills: (vol.skills || []).join(', '),
  });
  const [savingProfile, setSavingProfile] = useState(false);

  const headers = { 'x-portal-token': token };

  const fetchShifts = useCallback(async () => {
    setLoading(true);
    try {
      const [openRes, myRes] = await Promise.all([
        axios.get(`${API}/shifts`, { headers }),
        axios.get(`${API}/my-shifts`, { headers }),
      ]);
      setOpenShifts(openRes.data || []);
      setMyShifts(myRes.data.shifts || []);
      setTotalHours(myRes.data.totalHours || 0);
      setHoursLog(myRes.data.hoursLog || []);
    } finally { setLoading(false); }
  }, [token]);

  const fetchTasks = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tasks`, { headers });
      setTasks(res.data || []);
    } catch { /* noop */ }
  }, [token]);
  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const markTaskDone = async (taskId: string) => {
    setTaskBusy(taskId);
    try {
      await axios.post(`${API}/tasks/${taskId}/done`, { note: taskNote[taskId] || '' }, { headers });
      await fetchTasks();
      setTaskNote(n => { const c = { ...n }; delete c[taskId]; return c; });
    } catch (err: any) {
      alert(err?.response?.data?.error || 'שגיאה');
    } finally { setTaskBusy(null); }
  };

  const reopenTask = async (taskId: string) => {
    setTaskBusy(taskId);
    try {
      await axios.post(`${API}/tasks/${taskId}/reopen`, {}, { headers });
      await fetchTasks();
    } catch (err: any) {
      alert(err?.response?.data?.error || 'שגיאה');
    } finally { setTaskBusy(null); }
  };

  const fetchGroups = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/groups`, { headers });
      setGroups(res.data || []);
      if (res.data?.length && !groupId) setGroupId(res.data[0]._id);
    } catch { /* noop */ }
  }, [token]); // eslint-disable-line
  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const currentGroup = groups.find(g => g._id === groupId);

  // Load existing attendance for the selected group+date (so it's editable)
  useEffect(() => {
    if (!groupId || !attDate) return;
    let live = true;
    axios.get(`${API}/groups/${groupId}/attendance?date=${attDate}`, { headers })
      .then(res => {
        if (!live) return;
        const rec = res.data?.record;
        const map: Record<string, { status: string; note: string }> = {};
        const g = groups.find(x => x._id === groupId);
        (g?.members || []).forEach((m: any) => {
          const existing = rec?.entries?.find((e: any) => e.memberId === String(m._id));
          map[String(m._id)] = { status: existing?.status || 'present', note: existing?.note || '' };
        });
        setAttEntries(map);
      })
      .catch(() => {});
    return () => { live = false; };
  }, [groupId, attDate, groups]); // eslint-disable-line

  const setMemberStatus = (memberId: string, status: string) =>
    setAttEntries(prev => ({ ...prev, [memberId]: { ...(prev[memberId] || { note: '' }), status } }));

  const saveAttendance = async () => {
    if (!currentGroup) return;
    setAttSaving(true);
    setAttMsg(null);
    try {
      const entries = (currentGroup.members || []).map((m: any) => ({
        memberId: String(m._id), name: m.name,
        status: attEntries[String(m._id)]?.status || 'present',
        note: attEntries[String(m._id)]?.note || '',
      }));
      const res = await axios.post(`${API}/groups/${groupId}/attendance`, { date: attDate, entries }, { headers });
      setAttMsg({ msg: `נשמר ✅ — ${res.data.present}/${res.data.total} נוכחים`, ok: true });
      setTimeout(() => setAttMsg(null), 4000);
    } catch (err: any) {
      setAttMsg({ msg: err?.response?.data?.error || 'שגיאה', ok: false });
    } finally { setAttSaving(false); }
  };

  const openTasksCount = tasks.filter(t => t.status === 'open').length;
  const pendingHours = hoursLog.filter(l => l.status === 'pending').reduce((s, l) => s + (l.hours || 0), 0);

  const submitHours = async () => {
    const h = Number(reportHours);
    if (!h || h <= 0) { setReportingMsg({ msg: 'יש להזין מספר שעות', ok: false }); return; }
    setSubmittingHours(true);
    setReportingMsg(null);
    try {
      const res = await axios.post(`${API}/hours`, { date: reportDate, hours: h, description: reportDesc }, { headers });
      setHoursLog(res.data.hoursLog || []);
      setReportHours(''); setReportDesc('');
      setReportingMsg({ msg: res.data.message || 'נשלח לאישור', ok: true });
      setTimeout(() => setReportingMsg(null), 4000);
    } catch (err: any) {
      setReportingMsg({ msg: err?.response?.data?.error || 'שגיאה', ok: false });
    } finally { setSubmittingHours(false); }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const payload = {
        phone: profileDraft.phone, area: profileDraft.area, availability: profileDraft.availability,
        skills: profileDraft.skills.split(',').map(s => s.trim()).filter(Boolean),
      };
      const res = await axios.put(`${API}/me`, payload, { headers });
      setVol(v => ({ ...v, ...res.data }));
      setEditingProfile(false);
    } catch (err: any) {
      alert(err?.response?.data?.error || 'שגיאה בשמירה');
    } finally { setSavingProfile(false); }
  };

  useEffect(() => { fetchShifts(); }, [fetchShifts]);

  const handleSignup = async (shiftId: string) => {
    try {
      const res = await axios.post(`${API}/shifts/${shiftId}/signup`, {}, { headers });
      setSignupMsg({ id: shiftId, msg: res.data.message, ok: true });
      fetchShifts();
      setTimeout(() => setSignupMsg(null), 3000);
    } catch (err: any) {
      setSignupMsg({ id: shiftId, msg: err?.response?.data?.error || 'שגיאה', ok: false });
      setTimeout(() => setSignupMsg(null), 3000);
    }
  };

  const handleCancel = async (shiftId: string) => {
    if (!confirm('לבטל הרשמה למשמרת זו?')) return;
    await axios.delete(`${API}/shifts/${shiftId}/signup`, { headers });
    fetchShifts();
  };

  const handleConfirmAttendance = async (shiftId: string) => {
    try {
      await axios.post(`${API}/shifts/${shiftId}/confirm`, {}, { headers });
      fetchShifts();
    } catch (err: any) {
      alert(err?.response?.data?.error || 'שגיאה');
    }
  };

  const isSignedUp = (shift: Shift) => shift.assignments.some((a: any) => a.volunteerId === vol._id);
  const spotsLeft = (shift: Shift) => (shift.maxVolunteers || 99) - shift.assignments.length;

  const formatDate = (d: string) => new Date(d).toLocaleDateString('he-IL', { weekday: 'short', day: 'numeric', month: 'long' });

  return (
    <div className="min-h-screen bg-[#F5F7FA] dark:bg-[#121212]" dir="rtl">
      {/* Header */}
      <div className="bg-gradient-to-l from-[#0D47A1] to-[#1976D2] text-white px-4 py-4 sticky top-0 z-10">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div>
            <p className="font-bold text-lg">שלום {vol.name} 👋</p>
            <p className="text-white/70 text-xs">{totalHours} שעות · מתנדב פעיל</p>
          </div>
          <button onClick={onLogout} className="p-2 bg-white/10 rounded-xl hover:bg-white/20 transition-all">
            <LogOut size={18} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-lg mx-auto px-4 pt-4">
        <div className="flex gap-1 bg-white dark:bg-[#1E1E1E] rounded-xl p-1 shadow-sm mb-4">
          {(([['shifts','📅 משמרות'],['my-shifts','⏱ השעות שלי'],['tasks','✅ משימות'],
             ...(groups.length ? [['group','👥 הקבוצה שלי']] : []),
             ['profile','👤 פרופיל']]) as [typeof tab, string][]).map(([t,l]) => (
            <button key={t} onClick={() => setTab(t)}
              className={`relative flex-1 py-2 text-xs font-semibold rounded-lg transition-all ${tab===t?'bg-[#1565C0] text-white shadow-sm':'text-gray-500 hover:text-gray-700'}`}>
              {t === 'tasks' && openTasksCount > 0 && (
                <span className="absolute -top-1 -left-1 bg-red-500 text-white text-[10px] rounded-full px-1.5 py-0.5 leading-none">{openTasksCount}</span>
              )}
              {l}
            </button>
          ))}
        </div>

        {/* Open Shifts Tab */}
        {tab === 'shifts' && (
          <div className="space-y-3 pb-8">
            {loading ? <p className="text-center py-8 text-gray-400">טוען...</p> :
            openShifts.length === 0 ? (
              <div className="text-center py-16 bg-white dark:bg-[#1E1E1E] rounded-2xl shadow-sm">
                <p className="text-4xl mb-2">🗓️</p>
                <p className="text-gray-500">אין משמרות פתוחות כרגע</p>
              </div>
            ) : openShifts.map(shift => {
              const signed = isSignedUp(shift);
              const spots = spotsLeft(shift);
              return (
                <div key={shift._id} className="bg-white dark:bg-[#1E1E1E] rounded-2xl shadow-sm p-4 border border-[#E0E0E0] dark:border-[#2C2C2C]">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-semibold text-gray-800 dark:text-white">{shift.title}</p>
                      {shift.projectName && <p className="text-xs text-gray-400 mt-0.5">📁 {shift.projectName}</p>}
                    </div>
                    {signed ? (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">✅ רשום</span>
                    ) : spots <= 0 ? (
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-medium">🕐 המתנה</span>
                    ) : (
                      <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full font-medium">{spots} מקומות</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs text-gray-500 mb-3">
                    <span className="flex items-center gap-1"><Calendar size={12} />{formatDate(shift.date)}</span>
                    <span className="flex items-center gap-1"><Clock size={12} />{shift.startTime}–{shift.endTime}</span>
                    {shift.location && <span className="flex items-center gap-1"><MapPin size={12} />{shift.location}</span>}
                    <span className="flex items-center gap-1"><Users size={12} />{shift.assignments.length}/{shift.maxVolunteers}</span>
                  </div>
                  {signupMsg?.id === shift._id && (
                    <p className={`text-xs mb-2 font-medium ${signupMsg.ok ? 'text-green-600' : 'text-red-500'}`}>{signupMsg.msg}</p>
                  )}
                  {signed ? (
                    <button onClick={() => handleCancel(shift._id)}
                      className="w-full py-2.5 border-2 border-red-200 text-red-500 font-semibold text-sm rounded-xl hover:bg-red-50 transition-all">
                      ביטול הרשמה
                    </button>
                  ) : (
                    <button onClick={() => handleSignup(shift._id)}
                      className="w-full py-2.5 bg-[#1565C0] hover:bg-[#0D47A1] text-white font-semibold text-sm rounded-xl transition-all">
                      {spots <= 0 ? 'הצטרף לרשימת המתנה' : 'הירשם למשמרת'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* My Shifts Tab */}
        {tab === 'my-shifts' && (
          <div className="space-y-3 pb-8">
            <div className="bg-gradient-to-l from-[#1565C0] to-[#6200EA] text-white rounded-2xl p-4 flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                <Clock size={28} />
              </div>
              <div>
                <p className="text-3xl font-extrabold">{totalHours}</p>
                <p className="text-white/80 text-sm">שעות התנדבות מאושרות</p>
              </div>
              {pendingHours > 0 && (
                <div className="mr-auto text-center bg-white/15 rounded-xl px-3 py-2">
                  <p className="text-xl font-extrabold">{pendingHours}</p>
                  <p className="text-white/80 text-[11px]">ממתינות לאישור</p>
                </div>
              )}
            </div>

            {/* Self-report hours */}
            <div className="bg-white dark:bg-[#1E1E1E] rounded-2xl shadow-sm p-4 border border-[#E0E0E0] dark:border-[#2C2C2C]">
              <p className="font-bold text-gray-800 dark:text-white mb-3">⏱ דיווח שעות</p>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <div>
                  <label className="block text-[11px] text-gray-500 mb-0.5">תאריך</label>
                  <input type="date" dir="ltr" value={reportDate} onChange={e => setReportDate(e.target.value)}
                    className="w-full px-2 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 text-left" />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-500 mb-0.5">מספר שעות</label>
                  <input type="number" min="0" step="0.5" dir="ltr" value={reportHours} onChange={e => setReportHours(e.target.value)}
                    placeholder="לדוגמה: 3" className="w-full px-2 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 text-left" />
                </div>
              </div>
              <input type="text" value={reportDesc} onChange={e => setReportDesc(e.target.value)}
                placeholder="על מה התנדבת? (אופציונלי)"
                className="w-full px-2 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 mb-2" />
              <button onClick={submitHours} disabled={submittingHours}
                className="w-full bg-[#1565C0] hover:bg-[#0d47a1] text-white text-sm font-bold py-2.5 rounded-xl transition-all disabled:opacity-50">
                {submittingHours ? 'שולח…' : 'שלח לאישור'}
              </button>
              {reportingMsg && (
                <p className={`text-xs mt-2 text-center font-medium ${reportingMsg.ok ? 'text-green-600' : 'text-red-500'}`}>{reportingMsg.msg}</p>
              )}
            </div>

            {/* Hours history */}
            {hoursLog.length > 0 && (
              <div className="bg-white dark:bg-[#1E1E1E] rounded-2xl shadow-sm p-4 border border-[#E0E0E0] dark:border-[#2C2C2C]">
                <p className="font-bold text-gray-800 dark:text-white mb-3">היסטוריית שעות</p>
                <div className="space-y-2">
                  {[...hoursLog].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map((l, i) => (
                    <div key={l._id || i} className="flex items-center justify-between text-sm border-b border-[#F0F0F0] dark:border-[#2C2C2C] pb-2 last:border-0">
                      <div>
                        <span className="font-bold">{l.hours} שעות</span>
                        {l.description && <span className="text-gray-500 text-xs"> · {l.description}</span>}
                        <div className="text-[11px] text-gray-400">{new Date(l.date).toLocaleDateString('he-IL')}</div>
                      </div>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${l.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                        {l.status === 'pending' ? '⏳ ממתין' : '✅ אושר'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {myShifts.length === 0 ? (
              <div className="text-center py-12 bg-white dark:bg-[#1E1E1E] rounded-2xl shadow-sm">
                <p className="text-4xl mb-2">📋</p>
                <p className="text-gray-500">עוד לא השתתפת במשמרות</p>
              </div>
            ) : myShifts.map(shift => (
              <div key={shift._id} className="bg-white dark:bg-[#1E1E1E] rounded-2xl shadow-sm p-4 border border-[#E0E0E0] dark:border-[#2C2C2C]">
                <p className="font-semibold text-gray-800 dark:text-white">{shift.title}</p>
                <div className="flex gap-3 text-xs text-gray-500 mt-1">
                  <span>{formatDate(shift.date)}</span>
                  <span>{shift.startTime}–{shift.endTime}</span>
                </div>
                {(() => {
                  const a = shift.assignments.find((x: any) => x.volunteerId === vol._id);
                  const isFuture = new Date(shift.date) > new Date();
                  return a && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${a.status==='confirmed'?'bg-green-100 text-green-700':a.status==='declined'?'bg-red-100 text-red-600':'bg-amber-100 text-amber-700'}`}>
                        {a.status==='confirmed'?'✅ אישרתי הגעה':a.status==='declined'?'❌ לא אושר':'⏳ ממתין לאישור'}
                      </span>
                      {isFuture && a.status !== 'confirmed' && (
                        <button onClick={() => handleConfirmAttendance(shift._id)}
                          className="text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-full transition-all">
                          אשר הגעה ✅
                        </button>
                      )}
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>
        )}

        {/* Tasks Tab */}
        {tab === 'tasks' && (
          <div className="space-y-3 pb-8">
            {tasks.length === 0 ? (
              <div className="text-center py-12 bg-white dark:bg-[#1E1E1E] rounded-2xl shadow-sm">
                <p className="text-4xl mb-2">✅</p>
                <p className="text-gray-500">אין משימות כרגע</p>
              </div>
            ) : (
              <>
                {tasks.filter(t => t.status === 'open').map(t => (
                  <div key={t._id} className="bg-white dark:bg-[#1E1E1E] rounded-2xl shadow-sm p-4 border border-[#E0E0E0] dark:border-[#2C2C2C]">
                    <p className="font-bold text-gray-800 dark:text-white">{t.title}</p>
                    {t.description && <p className="text-sm text-gray-500 mt-0.5">{t.description}</p>}
                    {t.dueDate && (
                      <p className="text-[11px] mt-1 text-amber-600">📅 עד {new Date(t.dueDate).toLocaleDateString('he-IL')}</p>
                    )}
                    <input
                      type="text" value={taskNote[t._id] || ''} onChange={e => setTaskNote(n => ({ ...n, [t._id]: e.target.value }))}
                      placeholder="הערה (אופציונלי)…"
                      className="w-full mt-3 px-2 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700" />
                    <button onClick={() => markTaskDone(t._id)} disabled={taskBusy === t._id}
                      className="w-full mt-2 bg-green-600 hover:bg-green-700 text-white text-sm font-bold py-2.5 rounded-xl transition-all disabled:opacity-50">
                      {taskBusy === t._id ? 'שומר…' : '✅ סמן כבוצע'}
                    </button>
                  </div>
                ))}

                {tasks.some(t => t.status === 'done') && (
                  <div className="pt-2">
                    <p className="text-xs font-semibold text-gray-400 mb-2">בוצעו</p>
                    {tasks.filter(t => t.status === 'done').map(t => (
                      <div key={t._id} className="bg-gray-50 dark:bg-[#181818] rounded-xl p-3 border border-[#F0F0F0] dark:border-[#2C2C2C] mb-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-600 dark:text-gray-300 line-through">{t.title}</p>
                            {t.completionNote && <p className="text-[11px] text-gray-400">{t.completionNote}</p>}
                            {t.completedAt && <p className="text-[10px] text-gray-400">✅ {new Date(t.completedAt).toLocaleDateString('he-IL')}</p>}
                          </div>
                          <button onClick={() => reopenTask(t._id)} disabled={taskBusy === t._id}
                            className="text-[11px] text-[#1565C0] hover:underline disabled:opacity-50">בטל</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Group attendance Tab */}
        {tab === 'group' && (
          <div className="space-y-3 pb-8">
            {/* group + date pickers */}
            <div className="bg-white dark:bg-[#1E1E1E] rounded-2xl shadow-sm p-4 border border-[#E0E0E0] dark:border-[#2C2C2C] space-y-2">
              {groups.length > 1 && (
                <select value={groupId} onChange={e => setGroupId(e.target.value)}
                  className="w-full px-2 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700">
                  {groups.map(g => <option key={g._id} value={g._id}>{g.name}</option>)}
                </select>
              )}
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500">תאריך מפגש</label>
                <input type="date" dir="ltr" value={attDate} onChange={e => setAttDate(e.target.value)}
                  className="flex-1 px-2 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 text-left" />
              </div>
            </div>

            {!currentGroup || (currentGroup.members || []).length === 0 ? (
              <div className="text-center py-12 bg-white dark:bg-[#1E1E1E] rounded-2xl shadow-sm">
                <p className="text-4xl mb-2">👥</p>
                <p className="text-gray-500">אין חניכים בקבוצה</p>
              </div>
            ) : (
              <>
                <div className="bg-white dark:bg-[#1E1E1E] rounded-2xl shadow-sm border border-[#E0E0E0] dark:border-[#2C2C2C] divide-y divide-[#F0F0F0] dark:divide-[#2C2C2C]">
                  {currentGroup.members.map((m: any) => {
                    const st = attEntries[String(m._id)]?.status || 'present';
                    const opts: [string, string, string][] = [
                      ['present', 'הגיע', 'bg-green-600'],
                      ['absent', 'נעדר', 'bg-red-500'],
                      ['excused', 'מאושר', 'bg-amber-500'],
                    ];
                    return (
                      <div key={m._id} className="flex items-center justify-between p-3 gap-2">
                        <span className="font-medium text-gray-800 dark:text-white text-sm truncate">{m.name}</span>
                        <div className="flex gap-1 flex-shrink-0">
                          {opts.map(([val, label, color]) => (
                            <button key={val} onClick={() => setMemberStatus(String(m._id), val)}
                              className={`text-xs px-2.5 py-1.5 rounded-lg font-semibold transition-all ${st === val ? `${color} text-white` : 'bg-gray-100 dark:bg-gray-700 text-gray-500'}`}>
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex items-center justify-between text-xs text-gray-500 px-1">
                  <span>נוכחים: {Object.values(attEntries).filter(e => e.status === 'present').length}/{currentGroup.members.length}</span>
                  <div className="flex gap-2">
                    <button onClick={() => { const map: any = {}; currentGroup.members.forEach((m: any) => map[String(m._id)] = { status: 'present', note: '' }); setAttEntries(map); }}
                      className="text-[#1565C0] hover:underline">סמן הכל נוכח</button>
                  </div>
                </div>

                <button onClick={saveAttendance} disabled={attSaving}
                  className="w-full bg-[#1565C0] hover:bg-[#0d47a1] text-white text-sm font-bold py-3 rounded-xl transition-all disabled:opacity-50">
                  {attSaving ? 'שומר…' : '💾 שמור נוכחות'}
                </button>
                {attMsg && <p className={`text-xs text-center font-medium ${attMsg.ok ? 'text-green-600' : 'text-red-500'}`}>{attMsg.msg}</p>}
              </>
            )}
          </div>
        )}

        {/* Profile Tab */}
        {tab === 'profile' && (
          <div className="space-y-4 pb-8">
            <div className="bg-white dark:bg-[#1E1E1E] rounded-2xl shadow-sm p-5 border border-[#E0E0E0] dark:border-[#2C2C2C]">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#1565C0] to-[#6200EA] flex items-center justify-center text-white text-xl font-bold">
                  {vol.name.charAt(0)}
                </div>
                <div>
                  <p className="font-bold text-lg text-gray-800 dark:text-white">{vol.name}</p>
                  <p className="text-gray-400 text-sm">{vol.email}</p>
                </div>
              </div>
              {editingProfile ? (
                <div className="space-y-3 text-sm">
                  <div>
                    <label className="block text-[11px] text-gray-500 mb-0.5">טלפון</label>
                    <input type="tel" dir="ltr" value={profileDraft.phone} onChange={e => setProfileDraft(d => ({ ...d, phone: e.target.value }))}
                      className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 text-left" />
                  </div>
                  <div>
                    <label className="block text-[11px] text-gray-500 mb-0.5">אזור</label>
                    <input type="text" value={profileDraft.area} onChange={e => setProfileDraft(d => ({ ...d, area: e.target.value }))}
                      className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700" />
                  </div>
                  <div>
                    <label className="block text-[11px] text-gray-500 mb-0.5">זמינות</label>
                    <input type="text" value={profileDraft.availability} onChange={e => setProfileDraft(d => ({ ...d, availability: e.target.value }))}
                      placeholder="לדוגמה: ימי א׳-ג׳ בבוקר"
                      className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700" />
                  </div>
                  <div>
                    <label className="block text-[11px] text-gray-500 mb-0.5">כישורים (מופרדים בפסיק)</label>
                    <input type="text" value={profileDraft.skills} onChange={e => setProfileDraft(d => ({ ...d, skills: e.target.value }))}
                      className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700" />
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button onClick={saveProfile} disabled={savingProfile}
                      className="flex-1 bg-[#1565C0] hover:bg-[#0d47a1] text-white font-bold py-2 rounded-xl transition-all disabled:opacity-50">
                      {savingProfile ? 'שומר…' : 'שמור'}
                    </button>
                    <button onClick={() => setEditingProfile(false)}
                      className="px-4 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-600 dark:text-gray-300">ביטול</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between py-2 border-b border-[#F0F0F0] dark:border-[#2C2C2C]">
                      <span className="text-gray-500">טלפון</span>
                      <span className="font-medium dir-ltr">{vol.phone || '—'}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-[#F0F0F0] dark:border-[#2C2C2C]">
                      <span className="text-gray-500">אזור</span>
                      <span className="font-medium">{vol.area || '—'}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-[#F0F0F0] dark:border-[#2C2C2C]">
                      <span className="text-gray-500">זמינות</span>
                      <span className="font-medium">{vol.availability || '—'}</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-gray-500">כישורים</span>
                      <span className="font-medium text-left">{vol.skills?.join(', ') || '—'}</span>
                    </div>
                  </div>
                  <button onClick={() => { setProfileDraft({ phone: vol.phone || '', area: vol.area || '', availability: vol.availability || '', skills: (vol.skills || []).join(', ') }); setEditingProfile(true); }}
                    className="w-full mt-4 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 text-gray-700 dark:text-gray-200 font-semibold py-2 rounded-xl transition-all">
                    ✏️ עריכת פרטים
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
const VolunteerPortalPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const magicToken = searchParams.get('token');
  const orgIdParam = searchParams.get('org') || '';

  const [authToken, setAuthToken] = useState<string | null>(() => localStorage.getItem('volunteer_portal_token'));
  const [vol, setVol] = useState<VolunteerInfo | null>(() => {
    try { return JSON.parse(localStorage.getItem('volunteer_portal_user') || 'null'); } catch { return null; }
  });
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');

  // Auto-verify magic link token from URL
  useEffect(() => {
    if (magicToken && !authToken) {
      setVerifying(true);
      axios.post(`${API}/verify-token`, { token: magicToken })
        .then(res => {
          localStorage.setItem('volunteer_portal_token', res.data.token);
          localStorage.setItem('volunteer_portal_user', JSON.stringify(res.data.volunteer));
          setAuthToken(res.data.token);
          setVol(res.data.volunteer);
          window.history.replaceState({}, '', '/volunteer-portal');
        })
        .catch(() => setError('הקישור לא תקין או פג תוקפו'))
        .finally(() => setVerifying(false));
    }
  }, [magicToken]);

  // Load full volunteer info
  useEffect(() => {
    if (authToken && vol) {
      axios.get(`${API}/me`, { headers: { 'x-portal-token': authToken } })
        .then(res => { setVol(res.data); localStorage.setItem('volunteer_portal_user', JSON.stringify(res.data)); })
        .catch(() => { /* token expired */ handleLogout(); });
    }
  }, [authToken]);

  const handleLogin = (token: string, volunteer: any) => {
    localStorage.setItem('volunteer_portal_token', token);
    localStorage.setItem('volunteer_portal_user', JSON.stringify(volunteer));
    setAuthToken(token);
    setVol(volunteer);
  };

  const handleLogout = () => {
    localStorage.removeItem('volunteer_portal_token');
    localStorage.removeItem('volunteer_portal_user');
    setAuthToken(null);
    setVol(null);
  };

  if (verifying) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0D47A1] to-[#1976D2]">
      <div className="text-white text-center"><div className="animate-spin rounded-full h-12 w-12 border-4 border-white border-t-transparent mx-auto mb-4" /><p>מאמת...</p></div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0D47A1] to-[#1976D2]" dir="rtl">
      <div className="bg-white rounded-3xl p-8 text-center max-w-sm">
        <p className="text-4xl mb-3">😕</p>
        <p className="font-bold text-gray-800 mb-2">{error}</p>
        <button onClick={() => setError('')} className="text-[#1565C0] text-sm hover:underline">נסה שוב</button>
      </div>
    </div>
  );

  if (!authToken || !vol) {
    return <LoginScreen orgId={orgIdParam} onLogin={handleLogin} />;
  }

  return <PortalApp token={authToken} vol={vol} onLogout={handleLogout} />;
};

export default VolunteerPortalPage;
